import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, CalendarDays, Trash2, Loader2, ChevronLeft, ChevronRight, CloudRain, Sparkles, Sun, Cloud, CloudSnow, Wand2, Check, Pencil, Mail } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUS_STYLE = {
  geplant: "bg-[#DBE7F1] text-[#1F3B54]",
  unterwegs: "bg-[#FDF3D8] text-[#8A6100]",
  erledigt: "bg-[#DCEBD4] text-[#3B5C2C]",
  verschoben: "bg-slate-100 text-slate-500",
};

const WEATHER_STYLE = {
  gut: "text-[#3B5C2C]",
  akzeptabel: "text-[#8A6100]",
  "eingeschränkt": "text-[#B85C38]",
  schlecht: "text-[#7A2E14]",
};

function WeatherBadge({ w, size = "xs" }) {
  if (!w?.available) return null;
  const c = (w.condition || "").toLowerCase();
  let Icon = Sun;
  if (c.includes("gewitter") || c.includes("regen") || c.includes("niesel")) Icon = CloudRain;
  else if (c.includes("schnee") || c.includes("griesel")) Icon = CloudSnow;
  else if (c.includes("nebel") || c.includes("bewölkt")) Icon = Cloud;
  const cls = WEATHER_STYLE[w.category] || "text-[#5C6B5D]";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${cls}`}>
      <Icon className={size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={2} />
      {Math.round(w.score)}/100
    </span>
  );
}

function startOfWeek(d) {
  const day = d.getDay() === 0 ? 6 : d.getDay() - 1;
  const r = new Date(d);
  r.setDate(d.getDate() - day);
  r.setHours(0, 0, 0, 0);
  return r;
}

function fmtDate(d) { return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" }); }
function fmtTime(iso) { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());

  const [reschedTarget, setReschedTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c, e] = await Promise.all([
        api.get("/appointments?include_weather=true"), api.get("/customers"), api.get("/employees"),
      ]);
      setItems(a.data); setCustomers(c.data); setEmployees(e.data);
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("blessing:appointments-updated", handler);
    return () => window.removeEventListener("blessing:appointments-updated", handler);
  }, []);


  const changeStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeAppt = async (id) => {
    if (!window.confirm("Termin wirklich löschen? Eingeladene Kunden erhalten eine Absage über Outlook.")) return;
    try { await api.delete(`/appointments/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const days = useMemo(() => {
    if (view === "day") return [anchor];
    if (view === "week") {
      const start = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
      });
    }
    // month
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const arr = [];
    for (let d = 1; d <= last.getDate(); d++) {
      arr.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
    }
    return arr;
  }, [view, anchor]);

  const groupedByDay = useMemo(() => {
    const byDay = {};
    items.forEach((a) => {
      const key = a.starts_at.slice(0, 10);
      byDay[key] = byDay[key] || [];
      byDay[key].push(a);
    });
    return byDay;
  }, [items]);

  const move = (delta) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setAnchor(d);
  };

  const headerLabel = useMemo(() => {
    if (view === "day") return anchor.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    if (view === "week") {
      const start = startOfWeek(anchor);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${end.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;
    }
    return anchor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  }, [view, anchor]);

  return (
    <div data-testid="crm-appointments">
      <div className="mb-5 md:mb-6 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">Termine & Touren</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">Einsatzplanung im Tages-, Wochen- oder Monatsraster.</p>
        </div>
        <Button
          className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
          onClick={() => setCreating(true)}
          data-testid="appointment-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Neuer Termin
        </Button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB] flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="cal-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="cal-today-btn">Heute</Button>
            <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="cal-next-btn"><ChevronRight className="w-4 h-4" /></Button>
            <div className="ml-3 font-display text-lg text-[#1A201A]">{headerLabel}</div>
          </div>
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="day" data-testid="cal-view-day">Tag</TabsTrigger>
              <TabsTrigger value="week" data-testid="cal-view-week">Woche</TabsTrigger>
              <TabsTrigger value="month" data-testid="cal-view-month">Monat</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-sm text-[#5C6B5D] p-8">Lädt…</div>
          ) : view === "month" ? (
            <MonthGrid days={days} anchor={anchor} groupedByDay={groupedByDay} />
          ) : (
            <div className={`grid ${view === "day" ? "grid-cols-1" : "grid-cols-1 md:grid-cols-7"} gap-3`}>
              {days.map((d) => {
                const key = d.toISOString().slice(0, 10);
                const list = groupedByDay[key] || [];
                const isToday = d.toDateString() === new Date().toDateString();
                return (
                  <div key={key} className="md:min-h-[180px]">
                    <div className={`text-xs font-medium mb-2 ${isToday ? "text-[#6B7F3E]" : "text-[#5C6B5D]"}`}>
                      {fmtDate(d)} {isToday && "· Heute"}
                    </div>
                    <div className="space-y-2">
                      {list.length === 0 && <div className="text-[11px] text-[#B8C1A9]">—</div>}
                      {list.map((a) => (
                        <div
                          key={a.id}
                          data-testid={`appt-item-${a.id}`}
                          className={`border rounded-lg p-2.5 hover:border-[#6B7F3E]/50 bg-[#FAFAF5] ${
                            a.weather?.available && a.weather.category === "schlecht"
                              ? "border-[#EEB596]"
                              : a.weather?.available && a.weather.category === "eingeschränkt"
                              ? "border-[#F5D876]"
                              : "border-[#E5E7EB]"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-[11px] font-medium text-[#6B7F3E]">
                              {fmtTime(a.starts_at)} – {fmtTime(a.ends_at)}
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_STYLE[a.status]}`}>
                              {a.status}
                            </span>
                          </div>
                          <div className="text-sm font-medium mt-1 line-clamp-2">{a.title}</div>
                          {a.customer_name && <div className="text-[11px] text-[#5C6B5D]">{a.customer_name}</div>}
                          {a.weather?.available && (
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <WeatherBadge w={a.weather} />
                              <span className="text-[10px] text-[#5C6B5D] truncate">
                                {a.weather.condition}
                              </span>
                              {(a.weather.category === "schlecht" || a.weather.category === "eingeschränkt") && (
                                <button
                                  data-testid={`appt-reschedule-${a.id}`}
                                  onClick={() => setReschedTarget(a)}
                                  className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-medium text-[#6B7F3E] hover:text-[#5A6B34]"
                                  title="Wetter-basierten Vorschlag holen"
                                >
                                  <Wand2 className="w-3 h-3" /> Verschieben
                                </button>
                              )}
                            </div>
                          )}
                          {a.assigned_employees?.length > 0 && (
                            <div className="text-[11px] text-[#4A5D4E] mt-1">👤 {a.assigned_employees.join(", ")}</div>
                          )}
                          <div className="flex items-center gap-1 mt-2">
                            <Select value={a.status} onValueChange={(v) => changeStatus(a.id, v)}>
                              <SelectTrigger className="h-6 text-[10px] px-2" data-testid={`appt-status-${a.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="geplant">Geplant</SelectItem>
                                <SelectItem value="unterwegs">Unterwegs</SelectItem>
                                <SelectItem value="erledigt">Erledigt</SelectItem>
                                <SelectItem value="verschoben">Verschoben</SelectItem>
                              </SelectContent>
                            </Select>
                            <button
                              onClick={() => setEditing(a)}
                              className="ml-auto text-[#5C6B5D] hover:text-[#2C3E2D]"
                              data-testid={`appt-edit-${a.id}`}
                              aria-label="Termin bearbeiten"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => removeAppt(a.id)}
                              className="text-[#B85C38] hover:text-[#7A2E14]"
                              data-testid={`appt-delete-${a.id}`}
                              aria-label="Termin löschen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AppointmentDialog
        open={creating}
        onOpenChange={setCreating}
        customers={customers}
        employees={employees}
        onCreated={load}
      />
      <AppointmentDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        customers={customers}
        employees={employees}
        onCreated={load}
        appointment={editing}
      />
      <RescheduleDialog
        appointment={reschedTarget}
        onOpenChange={(o) => !o && setReschedTarget(null)}
        onDone={load}
      />
    </div>
  );
}

function RescheduleDialog({ appointment, onOpenChange, onDone }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [current, setCurrent] = useState(null);
  const [notify, setNotify] = useState(true);
  const open = !!appointment;

  useEffect(() => {
    if (!open) { setSuggestion(null); setCurrent(null); return; }
    setLoading(true);
    api.get(`/appointments/${appointment.id}/suggest-reschedule`)
      .then((r) => {
        setSuggestion(r.data.suggestion);
        setCurrent(r.data.current_weather);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, appointment]);

  const accept = async () => {
    if (!suggestion) return;
    setBusy(true);
    try {
      const r = await api.post(`/appointments/${appointment.id}/reschedule`, {
        new_starts_at: suggestion.iso,
        reason: `Wetterbedingt: ${current?.condition || ""} → ${suggestion.weather?.condition || ""}`,
        notify_customer: notify,
      });
      if (r.data.email?.status === "no_email") {
        toast.warning(`Termin verschoben – ${r.data.email.message} Kunde wurde nicht per E-Mail informiert.`);
      } else {
        toast.success(notify ? "Termin verschoben und Kunde informiert" : "Termin verschoben");
      }
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#6B7F3E]" /> Wetter-Vorschlag
          </DialogTitle>
        </DialogHeader>
        {!appointment ? null : (
          <div className="space-y-4">
            <div className="text-sm">
              <div className="text-xs uppercase tracking-wider text-[#5C6B5D] mb-0.5">Termin</div>
              <div className="font-medium text-[#1A201A]">{appointment.title}</div>
              <div className="text-xs text-[#5C6B5D]">
                {new Date(appointment.starts_at).toLocaleString("de-DE")}
              </div>
            </div>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-[#5C6B5D]">
                <Loader2 className="w-4 h-4 animate-spin" /> Wetter wird geprüft…
              </div>
            )}
            {!loading && (
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFAF5]">
                  <div className="text-[10px] font-medium uppercase tracking-wider text-[#5C6B5D] mb-1">Aktuell</div>
                  <div className="text-xs text-[#1A201A] mb-2">
                    {new Date(appointment.starts_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  {current?.available && (
                    <>
                      <div className="text-[11px]">{current.condition}</div>
                      <WeatherBadge w={current} />
                      <div className="text-[10px] text-[#5C6B5D] mt-1">{current.reasons}</div>
                    </>
                  )}
                </div>
                <div className={`rounded-lg p-3 ${suggestion ? "border-2 border-[#6B7F3E] bg-[#F5F6F0]" : "border border-dashed border-[#E5E7EB] bg-[#FAFAF5]"}`}>
                  <div className="text-[10px] font-medium uppercase tracking-wider text-[#6B7F3E] mb-1">Empfehlung</div>
                  {suggestion ? (
                    <>
                      <div className="text-xs font-medium text-[#1A201A] mb-2">
                        {new Date(suggestion.iso).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="text-[11px]">{suggestion.weather?.condition}</div>
                      <WeatherBadge w={suggestion.weather} />
                      <div className="text-[10px] text-[#5C6B5D] mt-1">{suggestion.weather?.reasons}</div>
                    </>
                  ) : (
                    <div className="text-xs text-[#5C6B5D]">
                      Kein besserer Slot in den nächsten 10 Tagen gefunden.
                    </div>
                  )}
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-[#2C3E2D] cursor-pointer">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                data-testid="resched-notify-checkbox"
                className="accent-[#6B7F3E]"
              />
              Kunde per E-Mail informieren
            </label>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
            onClick={accept}
            disabled={busy || !suggestion}
            data-testid="resched-accept-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Umbuchen</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MonthGrid({ days, anchor, groupedByDay }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startPad = (first.getDay() === 0 ? 7 : first.getDay()) - 1;
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  days.forEach((d) => cells.push(d));
  return (
    <div>
      <div className="grid grid-cols-7 mb-2">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((w) => (
          <div key={w} className="text-[11px] text-[#5C6B5D] font-medium px-2">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="min-h-[80px] bg-[#FAFAF5] rounded" />;
          const list = groupedByDay[d.toISOString().slice(0, 10)] || [];
          const isToday = d.toDateString() === new Date().toDateString();
          return (
            <div
              key={d.toISOString()}
              className={`min-h-[80px] border rounded-lg p-1.5 text-[11px] ${isToday ? "border-[#6B7F3E] bg-[#F5F6F0]" : "border-[#E5E7EB]"}`}
            >
              <div className={`text-xs font-medium mb-1 ${isToday ? "text-[#6B7F3E]" : "text-[#1A201A]"}`}>{d.getDate()}</div>
              {list.slice(0, 3).map((a) => (
                <div key={a.id} className="truncate rounded bg-[#6B7F3E]/10 text-[#2C3E2D] px-1 mb-0.5">
                  {fmtTime(a.starts_at)} {a.title.slice(0, 14)}
                </div>
              ))}
              {list.length > 3 && <div className="text-[10px] text-[#5C6B5D]">+{list.length - 3} weitere</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const pad2 = (n) => String(n).padStart(2, "0");
const localDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const localTime = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

function addressOf(c) {
  if (!c) return "";
  const city = `${c.address_zip || ""} ${c.address_city || ""}`.trim();
  return [c.address_street, city].filter(Boolean).join(", ");
}

// Rückmeldung zur Kunden-E-Mail (Terminbestätigung mit Kalendereinladung) nach dem Speichern
function notifyToast(res, invited, isEdit) {
  const base = isEdit ? "Termin aktualisiert" : "Termin angelegt";
  if (!invited) { toast.success(base); return; }
  const { calendar, confirmation, no_email: noEmail } = res || {};
  if (noEmail) {
    toast.warning(`${base} – ${noEmail} Es wurde keine Einladung versendet.`);
  } else if (calendar === "unchanged") {
    toast.success(`${base} – nur interne Angaben geändert, Kunde wird nicht benachrichtigt`);
  } else if (calendar === "simulated") {
    toast.warning(`${base} – E-Mail NICHT versendet (Microsoft Graph nicht konfiguriert)`);
  } else if (calendar === "error" && confirmation === "sent") {
    toast.warning(`${base} – Kalendereinladung fehlgeschlagen, Terminbestätigung ohne Einladung versendet`);
  } else if (calendar === "error") {
    toast.error(`${base}, aber der Versand an den Kunden ist fehlgeschlagen`);
  } else if (isEdit) {
    toast.success(`${base} – Kunde erhält eine aktualisierte Einladung`);
  } else {
    toast.success(`${base} – Terminbestätigung mit Kalendereinladung versendet`);
  }
}

// appointment gesetzt → Bearbeiten-Modus
function AppointmentDialog({ open, onOpenChange, customers, employees, onCreated, appointment = null }) {
  const [title, setTitle] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [date, setDate] = useState(localDate(new Date()));
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState(60);
  const [location, setLocation] = useState("");
  const [assigned, setAssigned] = useState([]);
  const [notes, setNotes] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (appointment) {
      const s = new Date(appointment.starts_at);
      const e = new Date(appointment.ends_at);
      setTitle(appointment.title || ""); setCustomerId(appointment.customer_id || "");
      setDate(localDate(s)); setTime(localTime(s));
      setDuration(Math.max(15, Math.round((e - s) / 60_000)));
      setLocation(appointment.location || "");
      setAssigned(appointment.assigned_employee_ids || []); setNotes(appointment.notes || "");
      setNotify(!!customers.find((c) => c.id === appointment.customer_id)?.email);
    } else {
      setTitle(""); setCustomerId(""); setDate(localDate(new Date()));
      setTime("09:00"); setDuration(60); setLocation(""); setAssigned([]); setNotes("");
      setNotify(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment]);

  const customer = customers.find((c) => c.id === customerId);

  const onCustomerChange = (id) => {
    const prev = customers.find((c) => c.id === customerId);
    const next = customers.find((c) => c.id === id);
    // Ort mit der Kundenadresse vorbelegen, solange er nicht von Hand geändert wurde
    if (!location || location === addressOf(prev)) setLocation(addressOf(next));
    // Einladung nur vorauswählen, wenn eine E-Mail hinterlegt ist
    setNotify(!!next?.email);
    setCustomerId(id);
  };

  const toggleEmp = (id) => {
    setAssigned((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id]);
  };

  const save = async () => {
    if (!title) { toast.error("Titel angeben"); return; }
    const start = new Date(`${date}T${time}:00`);
    if (Number.isNaN(start.getTime())) { toast.error("Datum und Uhrzeit angeben"); return; }
    const end = new Date(start.getTime() + (Number(duration) || 60) * 60_000);
    const invited = notify && !!customerId;
    const body = {
      title,
      customer_id: customerId || null,
      starts_at: start.toISOString(),
      ends_at: end.toISOString(),
      location: location || null,
      assigned_employee_ids: assigned,
      notes: notes || null,
      notify_customer: invited,
    };
    setSaving(true);
    try {
      const r = appointment
        ? await api.put(`/appointments/${appointment.id}`, body)
        : await api.post("/appointments", body);
      notifyToast(r.data, invited, !!appointment);
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{appointment ? "Termin bearbeiten" : "Neuer Termin"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto p-1">
          <div>
            <Label>Titel *</Label>
            <Input className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="appt-title-input" placeholder="z. B. Rasenpflege bei Familie Weber" />
          </div>
          <div>
            <Label>Kunde (optional)</Label>
            <Select value={customerId} onValueChange={onCustomerChange}>
              <SelectTrigger className="mt-1.5" data-testid="appt-customer-select">
                <SelectValue placeholder="Kunde wählen" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Datum</Label>
              <Input type="date" className="mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appt-date-input" />
            </div>
            <div>
              <Label>Uhrzeit</Label>
              <Input type="time" className="mt-1.5" value={time} onChange={(e) => setTime(e.target.value)} data-testid="appt-time-input" />
            </div>
            <div>
              <Label>Dauer (Min)</Label>
              <Input type="number" step="30" className="mt-1.5" value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="appt-duration-input" />
            </div>
          </div>
          <div>
            <Label>Ort</Label>
            <Input className="mt-1.5" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Adresse des Einsatzorts" data-testid="appt-location-input" />
          </div>
          {customerId && (
            <label
              htmlFor="appt-notify"
              className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6F0] px-3 py-2.5 transition cursor-pointer hover:border-[#6B7F3E]/60"
              data-testid="appt-notify-row"
            >
              <Checkbox
                id="appt-notify"
                checked={notify}
                onCheckedChange={(v) => setNotify(v === true)}
                data-testid="appt-notify-checkbox"
                className="mt-0.5"
              />
              <div className="text-sm text-[#2C3E2D] leading-snug">
                <div className="font-medium flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-[#6B7F3E]" />
                  {appointment ? "Kunde über Änderungen informieren" : "Termin bestätigen und Kunde einladen"}
                </div>
                <div className="text-xs text-[#5C6B5D] mt-0.5">
                  {appointment
                    ? `Bei geänderter Zeit oder geändertem Ort erhält ${customer?.email || "der Kunde"} eine aktualisierte Kalendereinladung.`
                    : `Eine E-Mail an ${customer?.email || "den Kunden"}: Terminbestätigung mit echter Outlook-Kalendereinladung (Vorlage „Terminbestätigung & Einladung“).`}
                </div>
              </div>
            </label>
          )}
          <div>
            <Label>Mitarbeiter zuweisen</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {employees.length === 0 && <div className="text-xs text-[#5C6B5D]">Noch keine Mitarbeiter angelegt.</div>}
              {employees.map((e) => (
                <button
                  key={e.id} type="button" onClick={() => toggleEmp(e.id)}
                  data-testid={`appt-emp-toggle-${e.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    assigned.includes(e.id) ? "bg-[#6B7F3E] text-white border-[#6B7F3E]" : "bg-white border-[#E5E7EB] hover:border-[#6B7F3E]"
                  }`}
                >
                  {e.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Notizen</Label>
            <Textarea className="mt-1.5" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="appt-notes-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white" onClick={save} disabled={saving} data-testid="appt-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarDays className="w-4 h-4 mr-1" /> Termin speichern</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
